import { getSelectedCountryCode } from "@utils/countryUtils";
import { getValidAccessToken, check401 } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

const BASE_URL = getApiOrigin();
const DISCOVER_ENDPOINT = "/api/v1/discover/get-url-links";
const DISCOVER_NEWS_ENDPOINT = "/api/v1/discover/";
const DISCOVER_ASYNC_ENDPOINT = "/api/v1/discover/async";
const DISCOVER_JOB_ENDPOINT = "/api/v1/discover/jobs";
const CONVERSATION_ENDPOINT = "/api/v1/discover/conversation";
const SOURCE_PREVIEW_ENDPOINT = "/api/v1/discover/source-preview";
const SOM_CHANGES_ENDPOINT = "/api/v1/som-sections/changes";
const STATE_REGULATORY_UPDATES_ENDPOINT = "/api/v1/regulatory-updates/state";
const USER_INTERESTS_ENDPOINT = "/api/v1/discover/user-interests";

const DISCOVER_POLL_INTERVAL_MS = 3000;
const DISCOVER_POLL_MAX_ATTEMPTS = 150;

/** Dedupe concurrent identical Discover news requests (StrictMode, effect re-runs). */
const inflightDiscoverNews = new Map<string, Promise<ConversationResponse>>();

/**
 * Build a user-facing error message from a non-OK response body.
 * Parses JSON when possible; otherwise returns raw text (e.g. gateway HTML).
 */
export function messageFromApiErrorBody(
  statusText: string,
  bodyText: string,
): string {
  let message = statusText;
  const trimmed = bodyText.trim();
  if (!trimmed) {
    return message;
  }
  try {
    const data = JSON.parse(trimmed) as {
      detail?: unknown;
      message?: unknown;
    };
    if (typeof data?.detail === "string" && data.detail.trim().length > 0) {
      return data.detail;
    }
    if (typeof data?.message === "string" && data.message.trim().length > 0) {
      return data.message;
    }
    return JSON.stringify(data);
  } catch {
    return trimmed;
  }
}

/** Read the response body once, then parse JSON (avoids double-read on error paths). */
async function parseJsonResponse<T>(response: Response): Promise<T> {
  const bodyText = await response.text();
  if (!response.ok) {
    throw new Error(messageFromApiErrorBody(response.statusText, bodyText));
  }
  if (!bodyText.trim()) {
    throw new Error("Empty response from API");
  }
  try {
    return JSON.parse(bodyText) as T;
  } catch {
    throw new Error("Invalid JSON response from API");
  }
}

function discoverAuthHeaders(): Record<string, string> {
  const token = getValidAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

function normalizeCategoriesForDiscover(
  params: GetNewsParams,
): Array<{ name: string; urls: string[] }> {
  if (params.categories) {
    return params.categories
      .map((c) => ({
        name: c.name,
        urls: [...c.urls].map((u) => u.trim()).filter(Boolean).sort(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  const urls = (params.url ?? "")
    .split(",")
    .map((u) => u.trim())
    .filter(Boolean)
    .sort();
  return [{ name: params.linkName ?? "", urls }];
}

/** Stable key for in-flight deduplication of Discover news fetches. */
export function buildDiscoverInflightKey(params: GetNewsParams): string {
  const categories = normalizeCategoriesForDiscover(params);
  return JSON.stringify({
    user_id: params.user_id,
    industry_type: params.industry_type,
    query: params.query.trim(),
    categories,
  });
}

export interface DiscoverLink {
  id: number;
  pretty_name: string;
  url: string;
  industry_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DiscoverLinksResponse {
  links: DiscoverLink[];
  total_count: number;
  industry_type: string;
}

export interface GetUrlLinksParams {
  user_id: number;
  industry_type: string;
}

export interface ConversationParams {
  user_id: number;
  industry_type: string;
  query: string;
  url: string;
  conversation_id?: string | null;
}

export interface ConversationSource {
  title: string;
  url: string;
  description: string;
}

export interface NewsItem {
  title: string;
  url: string;
  date: string;
  description: string;
  source: string;
}

export interface ArticleSource {
  name: string;
  url: string;
}

export interface Article {
  id: string;
  headline: string;
  bodyText: string;
  publishedTime: string;
  image: string;
  /** URL this article was scraped from (discover API). Used for conversion API. Never blank when from discover. */
  source_url?: string;
  sources: (string | ArticleSource)[];
  categories: string[];
  featured: boolean;
  likes: number;
  isLiked: boolean;
  fullContent?: {
    introduction?: string;
    sections?: Array<{
      title: string;
      content: string;
    }>;
  };
  suggested_questions?: string[];
}

export interface ConversationResponse {
  summary: string;
  sources: ConversationSource[];
  images: any[];
  query: string;
  url: string | null;
  urls?: string[];
  industry_type: string;
  timestamp: string;
  conversation_id?: string;
  is_follow_up_question?: boolean;
  suggested_follow_ups?: string[];
  news_items?: NewsItem[];
  articles?: Article[];
  /** Global administrators only — listing-phase prompt (alias for llmPromptsBySection.listing). */
  llmPrompt?: string;
  llmInvokeWallSec?: number;
  llmPromptsBySection?: Record<string, string>;
  llmInvokeWallSecondsBySection?: Record<string, number>;
}

export type DiscoverJobStatus = "pending" | "running" | "complete" | "failed";

export interface DiscoverJobStartResponse {
  job_id: string;
  status: DiscoverJobStatus;
  poll_url: string;
}

export interface DiscoverJobStatusResponse {
  job_id: string;
  status: DiscoverJobStatus;
  cache_key?: string | null;
  error?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  wall_sec?: number | null;
  result?: ConversationResponse | null;
}

// --- SOM Changes (Rules) API ---
export interface SomChange {
  section_id: number;
  tag: string;
  document_id: number;
  created_at: string;
  updated_at: string;
  section_hash: string;
  revision: string;
  issued_date: string | null;
  effective_date: string | null;
  implementation_date: string | null;
  paragraph_identifier: string | null;
  requirement_text: string | null;
  interpretative_guidelines: string[] | null;
  survey_procedures: string[] | null;
  appendix_letter: string | null;
  file_path: string | null;
  document_hash: string | null;
  document_date: string | null;
  change_type: string;
  hours_since_change: number | null;
  url: string | null;
}

export interface DocumentSummary {
  appendix_letter: string;
  facility_type_title: string | null;
  topic: string | null;
}

export interface SomChangesResponse {
  result: {
    changes: SomChange[];
    document_summaries?: DocumentSummary[];
    // The backend may return more meta fields; we keep the type open-ended
    [k: string]: any;
  };
  http_status?: number;
}

export interface GetSomChangesParams {
  change_type?: string; // e.g. "updated" | "created"
  limit?: number; // default 10
  country_code?: string; // default "USA"
  industry?: string; // default "healthcare"
  facility_type_slug?: string;
}

export async function getUrlLinks(
  params: GetUrlLinksParams
): Promise<DiscoverLinksResponse> {
  try {
    const queryParams = new URLSearchParams({
      user_id: params.user_id.toString(),
      industry_type: params.industry_type,
    });

    const headers = discoverAuthHeaders();
    delete headers["Content-Type"];

    const response = await fetch(
      `${BASE_URL}${DISCOVER_ENDPOINT}?${queryParams}`,
      {
        method: "GET",
        headers,
      }
    );

    check401(response);

    return parseJsonResponse<DiscoverLinksResponse>(response);
  } catch (error) {
    console.error("Error fetching URL links:", error);
    throw error;
  }
}

export interface GetNewsParams {
  user_id: number;
  industry_type: string;
  query: string;
  url?: string; // comma-separated URLs for single-category; ignored when categories is provided
  linkName?: string; // pretty_name for single-category; ignored when categories is provided
  categories?: Array<{ name: string; urls: string[] }>; // pre-built categories; takes priority over url/linkName
}

async function pollDiscoverJob(
  jobId: string,
  headers: Record<string, string>,
): Promise<ConversationResponse> {
  for (let attempt = 0; attempt < DISCOVER_POLL_MAX_ATTEMPTS; attempt += 1) {
    const response = await fetch(
      `${BASE_URL}${DISCOVER_JOB_ENDPOINT}/${jobId}`,
      { method: "GET", headers: { ...headers, Accept: "application/json" } },
    );
    check401(response);
    const statusBody = await parseJsonResponse<DiscoverJobStatusResponse>(
      response,
    );
    if (statusBody.status === "complete" && statusBody.result) {
      return statusBody.result;
    }
    if (statusBody.status === "failed") {
      throw new Error(statusBody.error?.trim() || "Discover analysis failed");
    }
    if (attempt < DISCOVER_POLL_MAX_ATTEMPTS - 1) {
      await new Promise((resolve) => setTimeout(resolve, DISCOVER_POLL_INTERVAL_MS));
    }
  }
  throw new Error(
    "Discover analysis timed out while waiting for results. Please try again.",
  );
}

async function getNewsFromUrlAsync(
  params: GetNewsParams,
): Promise<ConversationResponse> {
  const headers = discoverAuthHeaders();
  const categories = normalizeCategoriesForDiscover(params);
  const payload = {
    user_id: params.user_id,
    industry_type: params.industry_type,
    query: params.query,
    categories,
  };

  const startResponse = await fetch(`${BASE_URL}${DISCOVER_ASYNC_ENDPOINT}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  check401(startResponse);
  const started = await parseJsonResponse<DiscoverJobStartResponse>(startResponse);
  return pollDiscoverJob(started.job_id, headers);
}

/**
 * Fetch news from the discover API via async job (ACA consumption ingress safe).
 * Supply `categories` directly for multi-category requests, or `url` + `linkName` for single-category.
 */
export async function getNewsFromUrl(
  params: GetNewsParams
): Promise<ConversationResponse> {
  const inflightKey = buildDiscoverInflightKey(params);
  const existing = inflightDiscoverNews.get(inflightKey);
  if (existing) {
    return existing;
  }

  const promise = (async () => {
    try {
      return await getNewsFromUrlAsync(params);
    } catch (error) {
      console.error("Error fetching news from URL:", error);
      throw error;
    } finally {
      inflightDiscoverNews.delete(inflightKey);
    }
  })();

  inflightDiscoverNews.set(inflightKey, promise);
  return promise;
}

export async function sendConversation(
  params: ConversationParams
): Promise<ConversationResponse> {
  try {
    const headers = discoverAuthHeaders();

    // Build payload without conversation_id if it's null or undefined
    const payload: Record<string, any> = {
      user_id: params.user_id,
      industry_type: params.industry_type,
      query: params.query,
      url: params.url,
    };

    // Only include conversation_id if it's not null or undefined
    if (params.conversation_id !== null && params.conversation_id !== undefined) {
      payload.conversation_id = params.conversation_id;
    }

    const response = await fetch(`${BASE_URL}${CONVERSATION_ENDPOINT}`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    check401(response);

    return parseJsonResponse<ConversationResponse>(response);
  } catch (error) {
    console.error("Error sending conversation:", error);
    throw error;
  }
}

/** True when URL is an absolute cms.gov / www.cms.gov link (bug 10937 preview). */
export function isCmsGovSourceUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed || trimmed.includes(",")) return false;
  try {
    const parsed = new URL(trimmed);
    const host = parsed.hostname.toLowerCase();
    if (host !== "cms.gov" && host !== "www.cms.gov") return false;
    const scheme = parsed.protocol.toLowerCase();
    return scheme === "http:" || scheme === "https:";
  } catch {
    return false;
  }
}

export interface SourcePreviewResponse {
  url: string;
  title: string;
  text: string;
}

/** Fetch allowlisted CMS.gov page text via Rules (never open cms.gov in the browser). */
export async function getSourcePreview(url: string): Promise<SourcePreviewResponse> {
  const headers = discoverAuthHeaders();
  const query = new URLSearchParams({ url });
  const response = await fetch(
    `${BASE_URL}${SOURCE_PREVIEW_ENDPOINT}?${query.toString()}`,
    {
      method: "GET",
      headers,
    },
  );
  check401(response);
  return parseJsonResponse<SourcePreviewResponse>(response);
}

/**
 * Fetch recent SOM section changes from the external rules service.
 * Defaults mirror the provided query.
 */
export async function getSomChanges(
  params: GetSomChangesParams = {}
): Promise<SomChangesResponse> {
  const {
    change_type = "updated",
    limit = 10,
    industry = "healthcare",
  } = params;
  // Determine country dynamically from selected country (fallback to provided param, then "USA")
  const effectiveCountry =
    params.country_code && String(params.country_code).trim().length > 0
      ? params.country_code
      : (typeof getSelectedCountryCode === "function"
          ? getSelectedCountryCode()
          : "USA") || "USA";
  console.log("**********Params*************", params);
  const query = new URLSearchParams();
  query.set("change_type", change_type);
  query.set("limit", String(limit));
  query.set("country_code", effectiveCountry);
  query.set("industry", industry);
  if (params.facility_type_slug) {
    query.set("facility_type_slug", params.facility_type_slug);
  }

  const url = `${BASE_URL}${SOM_CHANGES_ENDPOINT}?${query.toString()}`;

  try {
    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      Accept: "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    check401(response);

    if (!response.ok) {
      const errText = await response.text().catch(() => "");
      throw new Error(
        `SOM changes fetch failed: ${response.status} ${response.statusText} ${errText}`
      );
    }

    const data = (await response.json()) as SomChangesResponse;
    return data;
  } catch (error) {
    console.error("Error fetching SOM changes:", error);
    throw error;
  }
}


// --- State Regulatory Updates (Rules) API — AB#10987 / AB#10985 ---
export interface StateRegulatoryUpdateItem {
  id: number;
  jurisdiction: string;
  state_code: string;
  title: string;
  summary?: string | null;
  topics?: string[] | null;
  source: string;
  url: string;
  facility_type_slugs?: string[] | null;
  published_at?: string | null;
  last_refreshed_at?: string | null;
}

export interface StateRegulatoryUpdateListResponse {
  items: StateRegulatoryUpdateItem[];
  total_count: number;
  state_code: string;
  facility_type_slug?: string | null;
}

export interface GetStateRegulatoryUpdatesParams {
  state_code: string;
  facility_type_slug?: string;
  limit?: number;
}

/**
 * Canonicalize short RHC/FQHC aliases for the Rules state-updates filter.
 */
export function normalizeStateUpdatesFacilitySlug(
  slug?: string | null,
): string | undefined {
  if (!slug) return undefined;
  const s = slug.trim().toLowerCase().replace(/-/g, "_").replace(/\s+/g, "_");
  if (!s) return undefined;
  if (s === "rhc") return "rural_health_clinic";
  if (s === "fqhc") return "federally_qualified_health_center";
  return s;
}

/**
 * Fetch curated state regulatory updates from Rules
 * (GET /api/v1/regulatory-updates/state).
 */
export async function getStateRegulatoryUpdates(
  params: GetStateRegulatoryUpdatesParams,
): Promise<StateRegulatoryUpdateListResponse> {
  const stateCode = (params.state_code || "").trim().toUpperCase();
  if (!stateCode) {
    throw new Error("state_code is required");
  }
  const limit = params.limit ?? 50;
  const slug = normalizeStateUpdatesFacilitySlug(params.facility_type_slug);

  const query = new URLSearchParams();
  query.set("state_code", stateCode);
  query.set("limit", String(limit));
  if (slug) {
    query.set("facility_type_slug", slug);
  }

  const url = `${BASE_URL}${STATE_REGULATORY_UPDATES_ENDPOINT}?${query.toString()}`;

  try {
    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      Accept: "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    check401(response);

    if (!response.ok) {
      const errText = await response.text().catch(() => "");
      throw new Error(
        `State regulatory updates fetch failed: ${response.status} ${response.statusText} ${errText}`,
      );
    }

    return (await response.json()) as StateRegulatoryUpdateListResponse;
  } catch (error) {
    console.error("Error fetching state regulatory updates:", error);
    throw error;
  }
}

export interface UserInterestsResponse {
  user_id: number;
  category_ids: string[];
}

/**
 * Fetch saved interest category IDs for the current user.
 */
export async function getUserInterests(userId: number): Promise<UserInterestsResponse> {
  const token = getValidAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(
    `${BASE_URL}${USER_INTERESTS_ENDPOINT}?user_id=${userId}`,
    { method: "GET", headers }
  );
  check401(response);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`GET user-interests failed: ${response.status} ${text}`);
  }

  return response.json() as Promise<UserInterestsResponse>;
}

/**
 * Save (replace) the interest category IDs for the current user.
 */
export async function saveUserInterests(userId: number, categoryIds: string[]): Promise<UserInterestsResponse> {
  const token = getValidAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${BASE_URL}${USER_INTERESTS_ENDPOINT}`, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId, category_ids: categoryIds }),
  });
  check401(response);

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`POST user-interests failed: ${response.status} ${text}`);
  }

  return response.json() as Promise<UserInterestsResponse>;
}
